import { arrayMove } from "@dnd-kit/sortable"
import { useCallback, useRef, useState } from "react"

import { patchUIPreferences } from "../../../User/services"
import {
  GC_DEFAULT_VIEW_ID,
  HOME_DEFAULT_VERSION,
  hasHomeDefault,
  homeDefaultVersionOf,
  hasStoredLayout,
  withGcDefaultView
} from "./gcDefaultView"
import type { UIPreferencesPatch } from "../../../User/redux/ui-preferences.types"
import {
  createEmptyFilterLabels,
  createEmptyFilters,
  parseActiveViewFilterLabels,
  parseActiveViewGlobalFilters,
  parseFilterLabels,
  parseGlobalFilters
} from "../../redux/globalFilters"
import type {
  DashboardConfigV3,
  DashboardView,
  FilterLabels,
  GlobalFilters
} from "../../redux/portfolio.types"
import type {
  DashboardConfigV4,
  DashboardLayoutItemV4,
  DashboardViewV4,
  WidgetInstanceV4
} from "../../redux/widgets.types"

type LayoutVersion = 3 | 4
type InstanceMap = Record<string, WidgetInstanceV4>

interface UseDashboardLayoutOptions {
  version?: LayoutVersion
  resolveInstance?: (tileId: string) => WidgetInstanceV4 | null
  fixedInstances?: WidgetInstanceV4[]
  // Which `UIPreferences` key this instance reads/writes its layout under.
  // Defaults to the single-branch Home's own `"dashboard_config"` — Company
  // HQ passes a different key so editing one layout can never overwrite
  // the other.
  preferenceKey?: string
}

const DEFAULT_PREFERENCE_KEY = "dashboard_config"

/** Order-independent, so a grid that hands its items back shuffled is not "dirty". */
const serializeLayout = (layout: DashboardLayoutItemV4[]): string =>
  [...layout]
    .map(
      (l) => `${l.i}:${l.x},${l.y},${l.w},${l.h},${l.s ?? ""},${l.label ?? ""}`
    )
    .sort()
    .join("|")

// The default key writes the dedicated top-level `dashboard_config` field
// (existing, unchanged, so no migration for the accounts already using it);
// any other key rides the generic per-surface `preferences` bag instead of
// adding a new top-level field for every future alternate layout.
function buildPreferencesPatch(
  preferenceKey: string,
  config: DashboardConfigV3 | DashboardConfigV4
): UIPreferencesPatch {
  if (preferenceKey === DEFAULT_PREFERENCE_KEY) {
    return { dashboard_config: config }
  }
  return { preferences: { [preferenceKey]: config } }
}

export interface UseDashboardLayoutReturn {
  views: DashboardView[]
  activeView: { id: string; name: string } | null
  activeViewId: string | null
  defaultViewId: string | null
  isEditing: boolean
  /** A save is in flight. Every editing control disables against this. */
  saving: boolean
  /** The draft differs from the layout editing began with. */
  isDirty: boolean
  order: string[]
  hidden: string[]
  /** Per-widget grid geometry for the active view's draft. */
  layout: DashboardLayoutItemV4[]
  globalFilters: GlobalFilters
  filterLabels: FilterLabels
  initFromConfig: (raw: unknown) => void
  /** The one-time migration decided GC Default needs its default widgets. */
  pendingDefaultSeed: boolean
  /**
   * The Home default version the stored config is on while a seed is
   * pending: `0` for a config never migrated (seed everything), otherwise the
   * version to add the later defaults on top of.
   */
  pendingSeedFromVersion: number
  /**
   * Seeds GC Default with these instances, marks the config and persists.
   * `append` adds them to GC Default's existing widgets instead of replacing
   * them — what a later default version does.
   */
  seedDefaultWidgets: (
    instances: WidgetInstanceV4[],
    options?: { append?: boolean }
  ) => Promise<void>
  /** True once a stored layout supplied an order — a user customisation. */
  hasStoredOrder: boolean
  persistGlobalFilters: (
    next: GlobalFilters,
    nextLabels?: FilterLabels,
    viewId?: string | null
  ) => Promise<void>
  createView: (name: string, from?: "current" | "default") => Promise<void>
  renameView: (id: string, name: string) => Promise<void>
  deleteView: (id: string) => Promise<void>
  setDefault: (id: string) => Promise<void>
  switchView: (id: string) => Promise<void>
  reorder: (activeId: string, overId: string) => void
  /** Replaces the whole geometry — what the grid hands back after a drag or resize. */
  setLayout: (next: DashboardLayoutItemV4[]) => void
  toggleHidden: (id: string) => void
  addTile: (id: string) => void
  addWidget: (instance: WidgetInstanceV4) => void
  registerInstances: (instances: WidgetInstanceV4[]) => void
  getInstance: (slug: string) => WidgetInstanceV4 | undefined
  startEdit: (effectiveOrder?: string[]) => void
  cancel: () => void
  save: () => Promise<void>
  /**
   * Clears the DRAFT back to the role/company defaults and stays in edit mode.
   * Nothing is written — `save` is still what persists. Distinct from
   * `restoreDefault`, which writes immediately and is what the legacy
   * `PortfolioDashboard` fallback still calls.
   */
  resetDraftToDefault: () => void
  /**
   * Replaces the DRAFT with an explicit default — GC Default's widgets and an
   * empty geometry, which the page reads as its default arrangement. Nothing
   * is written; Save layout still decides.
   */
  resetDraftTo: (draft: { order: string[]; hidden?: string[] }) => void
  restoreDefault: () => Promise<void>
}

function hydrateViewFilters<T extends DashboardView>(
  view: T,
  fallbackConfig: unknown
): T {
  return {
    ...view,
    globalFilters: Object.hasOwn(view, "globalFilters")
      ? parseGlobalFilters(view)
      : parseGlobalFilters(fallbackConfig),
    filterLabels: Object.hasOwn(view, "filterLabels")
      ? parseFilterLabels(view)
      : parseFilterLabels(fallbackConfig)
  }
}

function normalizeToV3(raw: unknown): DashboardConfigV3 {
  if (raw != null && typeof raw === "object" && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>

    if (obj.version === 3 && Array.isArray(obj.views)) {
      const config = raw as DashboardConfigV3
      return {
        ...config,
        views: config.views.map((view) => hydrateViewFilters(view, config))
      }
    }

    if (obj.version === 2) {
      return {
        version: 3,
        views: [
          {
            id: "default",
            name: "My View",
            order: Array.isArray(obj.order) ? (obj.order as string[]) : [],
            hidden: Array.isArray(obj.hidden) ? (obj.hidden as string[]) : []
          }
        ],
        activeViewId: "default",
        defaultViewId: null
      }
    }

    if (Array.isArray(obj.tiles)) {
      return {
        version: 3,
        views: [
          {
            id: "default",
            name: "My View",
            order: (obj.tiles as { id: string }[]).map((t) => t.id),
            hidden: []
          }
        ],
        activeViewId: "default",
        defaultViewId: null
      }
    }
  }

  return {
    version: 3,
    views: [{ id: "default", name: "My View", order: [], hidden: [] }],
    activeViewId: "default",
    defaultViewId: null
  }
}

function toV4View(
  view: DashboardView,
  instances: InstanceMap,
  fixed: WidgetInstanceV4[]
): DashboardViewV4 {
  const widgets = view.order
    .map((slug) => instances[slug])
    .filter((w): w is WidgetInstanceV4 => Boolean(w))
  const present = new Set(widgets.map((w) => w.slug))
  // The always-on charts live outside the reorderable order; append them so the
  // widgets endpoint resolves them for every view. An empty view is left empty —
  // that is the signal the backend reads as "reset me to the defaults".
  if (widgets.length) {
    for (const f of fixed) {
      if (!present.has(f.slug)) {
        widgets.push(instances[f.slug] ?? f)
        present.add(f.slug)
      }
    }
  }
  return {
    id: view.id,
    key: view.key ?? view.id,
    name: view.name,
    widgets,
    hidden: view.hidden,
    // A sibling of `widgets`: the backend stores this blob verbatim and its
    // own reader never looks here, so geometry survives the round trip.
    ...(view.layout?.length ? { layout: view.layout } : {}),
    globalFilters: view.globalFilters ?? createEmptyFilters(),
    filterLabels: view.filterLabels ?? createEmptyFilterLabels()
  }
}

function buildConfig(
  version: LayoutVersion,
  views: DashboardView[],
  activeViewId: string | null,
  defaultViewId: string | null,
  instances: InstanceMap,
  fixed: WidgetInstanceV4[],
  homeDefaultVersion?: number
): DashboardConfigV3 | DashboardConfigV4 {
  // The migration marker rides every write, so a config that was migrated and
  // then saved for any other reason stays marked.
  const marker = homeDefaultVersion === undefined ? {} : { homeDefaultVersion }
  if (version === 4) {
    return {
      version: 4,
      views: views.map((v) => toV4View(v, instances, fixed)),
      activeViewId,
      defaultViewId,
      ...marker
    }
  }
  return { version: 3, views, activeViewId, defaultViewId, ...marker }
}

interface LiftedLayout {
  views: DashboardView[]
  instances: InstanceMap
  activeViewId: string | null
  defaultViewId: string | null
}

function parseV4(
  config: DashboardConfigV4,
  fixed: WidgetInstanceV4[]
): LiftedLayout {
  const instances: InstanceMap = {}
  for (const f of fixed) instances[f.slug] = f
  const fixedSlugs = new Set(fixed.map((f) => f.slug))
  const views: DashboardView[] = (config.views ?? []).map((v) => {
    const order: string[] = []
    for (const w of v.widgets ?? []) {
      instances[w.slug] = {
        id: w.id ?? w.slug,
        slug: w.slug,
        type: w.type,
        dataSource: w.dataSource,
        config: w.config ?? {}
      }
      if (!fixedSlugs.has(w.slug)) order.push(w.slug)
    }
    return {
      id: v.id,
      key: v.key ?? v.id,
      name: v.name,
      order,
      hidden: v.hidden ?? [],
      layout: Array.isArray(v.layout) ? v.layout : undefined,
      globalFilters: Object.hasOwn(v, "globalFilters")
        ? parseGlobalFilters(v)
        : parseGlobalFilters(config),
      filterLabels: Object.hasOwn(v, "filterLabels")
        ? parseFilterLabels(v)
        : parseFilterLabels(config)
    }
  })
  return {
    views,
    instances,
    activeViewId: config.activeViewId,
    defaultViewId: config.defaultViewId
  }
}

function liftV3ToV4(
  base: DashboardConfigV3,
  resolveInstance: (tileId: string) => WidgetInstanceV4 | null,
  fixed: WidgetInstanceV4[]
): LiftedLayout {
  const instances: InstanceMap = {}
  for (const f of fixed) instances[f.slug] = f

  const views: DashboardView[] = base.views.map((v) => {
    const order: string[] = []
    for (const tileId of v.order) {
      const inst = resolveInstance(tileId)
      if (inst) {
        instances[inst.slug] = inst
        order.push(inst.slug)
      }
    }
    const hidden: string[] = []
    for (const tileId of v.hidden) {
      const inst = resolveInstance(tileId)
      if (inst) hidden.push(inst.slug)
    }
    return {
      id: v.id,
      key: v.id,
      name: v.name,
      order,
      hidden,
      globalFilters: v.globalFilters ?? createEmptyFilters(),
      filterLabels: v.filterLabels ?? createEmptyFilterLabels()
    }
  })
  return {
    views,
    instances,
    activeViewId: base.activeViewId,
    defaultViewId: base.defaultViewId
  }
}

function pickActiveId(
  views: DashboardView[],
  activeViewId: string | null,
  defaultViewId: string | null
): string | null {
  return (
    views.find((v) => v.id === activeViewId)?.id ??
    views.find((v) => v.id === defaultViewId)?.id ??
    views[0]?.id ??
    null
  )
}

async function commitConfig(
  views: DashboardView[],
  activeViewId: string | null,
  defaultViewId: string | null,
  order: string[],
  hidden: string[],
  globalFilters: GlobalFilters,
  filterLabels: FilterLabels,
  version: LayoutVersion,
  instances: InstanceMap,
  fixed: WidgetInstanceV4[],
  preferenceKey: string,
  homeDefaultVersion?: number
): Promise<void> {
  const updatedViews = views.map((v) =>
    v.id === activeViewId
      ? { ...v, order, hidden, globalFilters, filterLabels }
      : v
  )
  const config = buildConfig(
    version,
    updatedViews,
    activeViewId,
    defaultViewId,
    instances,
    fixed,
    homeDefaultVersion
  )
  const response = await patchUIPreferences(
    buildPreferencesPatch(preferenceKey, config)
  )
  if (!response.ok) throw new Error("persist failed")
}

export function useDashboardLayout(
  options?: UseDashboardLayoutOptions
): UseDashboardLayoutReturn {
  const optsRef = useRef(options)
  optsRef.current = options
  const versionRef = useRef<LayoutVersion>(options?.version ?? 3)
  versionRef.current = options?.version ?? 3
  const fixedRef = useRef<WidgetInstanceV4[]>(options?.fixedInstances ?? [])
  fixedRef.current = options?.fixedInstances ?? []
  const preferenceKeyRef = useRef<string>(
    options?.preferenceKey ?? DEFAULT_PREFERENCE_KEY
  )
  preferenceKeyRef.current = options?.preferenceKey ?? DEFAULT_PREFERENCE_KEY
  const instancesRef = useRef<InstanceMap>({})

  // The migration marker as read from (or set on) the stored config. Starts
  // undefined; `initFromConfig` reads it, `seedDefaultWidgets` sets it.
  const homeDefaultVersionRef = useRef<number | undefined>(undefined)
  // True between the one-time migration deciding GC Default must be seeded
  // and the dashboard supplying the catalog-resolved instances to seed it.
  const [pendingDefaultSeed, setPendingDefaultSeed] = useState(false)
  const [pendingSeedFromVersion, setPendingSeedFromVersion] = useState(0)
  const [views, setViews] = useState<DashboardView[]>([])
  const [activeViewId, setActiveViewId] = useState<string | null>(null)
  const [defaultViewId, setDefaultViewId] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)
  const [order, setOrder] = useState<string[]>([])
  const [hasStoredOrder, setHasStoredOrder] = useState(false)
  const [hidden, setHidden] = useState<string[]>([])
  const [layout, setLayoutState] = useState<DashboardLayoutItemV4[]>([])
  const [globalFilters, setGlobalFiltersState] =
    useState<GlobalFilters>(createEmptyFilters())
  const [filterLabels, setFilterLabelsState] = useState<FilterLabels>(
    createEmptyFilterLabels()
  )

  const live = useRef({
    views,
    activeViewId,
    defaultViewId,
    order,
    hidden,
    layout,
    globalFilters,
    filterLabels
  })
  live.current = {
    views,
    activeViewId,
    defaultViewId,
    order,
    hidden,
    layout,
    globalFilters,
    filterLabels
  }

  const snapshot = useRef<{
    order: string[]
    hidden: string[]
    layout: DashboardLayoutItemV4[]
  } | null>(null)
  const hasInitialized = useRef(false)

  const found = views.find((v) => v.id === activeViewId)
  const activeView = found ? { id: found.id, name: found.name } : null

  const initFromConfig = useCallback((raw: unknown) => {
    if (hasInitialized.current) return
    hasInitialized.current = true
    setIsEditing(false)
    snapshot.current = null

    if (versionRef.current === 4) {
      const opts = optsRef.current
      const fixed = opts?.fixedInstances ?? []
      const resolve = opts?.resolveInstance ?? (() => null)
      const isV4 =
        raw != null &&
        typeof raw === "object" &&
        !Array.isArray(raw) &&
        (raw as { version?: unknown }).version === 4
      const lifted = isV4
        ? parseV4(raw as DashboardConfigV4, fixed)
        : liftV3ToV4(normalizeToV3(raw), resolve, fixed)

      instancesRef.current = lifted.instances
      // The one-time migration. A config without the current marker gains GC
      // Default (its own views untouched) and opens on it; the dashboard then
      // seeds the default widgets from the catalog and persists, marking the
      // config so this never runs again. A marked config is the user's own.
      const migrated = hasHomeDefault(raw)
      const storedVersion = homeDefaultVersionOf(raw)
      homeDefaultVersionRef.current = migrated ? storedVersion : undefined
      const seeded = withGcDefaultView(
        lifted.views,
        pickActiveId(lifted.views, lifted.activeViewId, lifted.defaultViewId),
        hasStoredLayout(raw),
        migrated
      )
      // A migrated config on an older default version still gets that
      // version's NEW widgets — added, never re-seeded over the user's own.
      setPendingDefaultSeed(
        !migrated || (storedVersion ?? 0) < HOME_DEFAULT_VERSION
      )
      setPendingSeedFromVersion(migrated ? (storedVersion ?? 0) : 0)
      const resolvedId = seeded.activeViewId
      const activeV = seeded.views.find((v) => v.id === resolvedId)
      setViews(seeded.views)
      setActiveViewId(resolvedId)
      setDefaultViewId(lifted.defaultViewId)
      setOrder(activeV?.order ?? [])
      setHidden(activeV?.hidden ?? [])
      setLayoutState(activeV?.layout ?? [])
      setHasStoredOrder((activeV?.order?.length ?? 0) > 0)
      setGlobalFiltersState(
        activeV
          ? parseGlobalFilters(activeV)
          : parseActiveViewGlobalFilters(raw)
      )
      setFilterLabelsState(
        activeV ? parseFilterLabels(activeV) : parseActiveViewFilterLabels(raw)
      )
      return
    }

    const config = normalizeToV3(raw)
    // The v3 reader backs the legacy Portfolio fallback, which has no sectioned
    // composition to migrate to: GC Default is offered, never forced here.
    const seededV3 = withGcDefaultView(
      config.views,
      pickActiveId(config.views, config.activeViewId, config.defaultViewId),
      hasStoredLayout(raw),
      true
    )
    const resolvedId = seededV3.activeViewId
    const activeV = seededV3.views.find((v) => v.id === resolvedId)
    setViews(seededV3.views)
    setActiveViewId(resolvedId)
    setDefaultViewId(config.defaultViewId)
    setOrder(activeV?.order ?? [])
    setHidden(activeV?.hidden ?? [])
    setLayoutState(activeV?.layout ?? [])
    setGlobalFiltersState(
      activeV ? parseGlobalFilters(activeV) : parseActiveViewGlobalFilters(raw)
    )
    setFilterLabelsState(
      activeV ? parseFilterLabels(activeV) : parseActiveViewFilterLabels(raw)
    )
  }, [])

  const persistGlobalFilters = useCallback(
    async (
      next: GlobalFilters,
      nextLabels?: FilterLabels,
      requestedViewId?: string | null
    ) => {
      const {
        views: v,
        activeViewId: aid,
        defaultViewId: did,
        order: o,
        hidden: h,
        filterLabels: currentLabels
      } = live.current
      const targetId = requestedViewId ?? aid
      const targetView = v.find((view) => view.id === targetId)
      if (!targetView) return

      const labels =
        nextLabels ??
        (targetId === aid ? currentLabels : parseFilterLabels(targetView))
      if (targetId !== aid) {
        const updatedViews = v.map((view) =>
          view.id === targetId
            ? { ...view, globalFilters: next, filterLabels: labels }
            : view
        )
        setViews(updatedViews)
        const config = buildConfig(
          versionRef.current,
          updatedViews,
          aid,
          did,
          instancesRef.current,
          fixedRef.current
        )
        const response = await patchUIPreferences(
          buildPreferencesPatch(preferenceKeyRef.current, config)
        )
        if (!response.ok) throw new Error("persist failed")
        return
      }

      setGlobalFiltersState(next)
      setFilterLabelsState(labels)
      // While editing, live order/hidden is an unsaved draft; persist the pre-edit
      // snapshot so a filter change never commits the draft ahead of Save/Cancel.
      const committed = snapshot.current ?? { order: o, hidden: h }
      const updatedViews = v.map((view) =>
        view.id === aid
          ? {
              ...view,
              order: committed.order,
              hidden: committed.hidden,
              globalFilters: next,
              filterLabels: labels
            }
          : view
      )
      setViews(updatedViews)
      await commitConfig(
        updatedViews,
        aid,
        did,
        committed.order,
        committed.hidden,
        next,
        labels,
        versionRef.current,
        instancesRef.current,
        fixedRef.current,
        preferenceKeyRef.current,
        homeDefaultVersionRef.current
      )
    },
    []
  )

  const createView = useCallback(
    async (name: string, from: "current" | "default" = "current") => {
      const {
        views: v,
        activeViewId: aid,
        defaultViewId: did,
        order: o,
        hidden: h,
        globalFilters: g,
        filterLabels: fl
      } = live.current

      const savedViews = v.map((view) =>
        view.id === aid
          ? { ...view, order: o, hidden: h, globalFilters: g, filterLabels: fl }
          : view
      )
      const srcView =
        from === "default"
          ? savedViews.find((x) => x.id === did)
          : savedViews.find((x) => x.id === aid)
      const srcOrder = srcView ? srcView.order : from === "default" ? [] : o
      const srcHidden = srcView ? srcView.hidden : from === "default" ? [] : h
      const srcFilters = srcView
        ? parseGlobalFilters(srcView)
        : from === "default"
          ? createEmptyFilters()
          : g
      const srcLabels = srcView
        ? parseFilterLabels(srcView)
        : from === "default"
          ? createEmptyFilterLabels()
          : fl

      const id = crypto.randomUUID()
      const newView: DashboardView = {
        id,
        key: id,
        name,
        order: srcOrder,
        hidden: srcHidden,
        globalFilters: srcFilters,
        filterLabels: srcLabels
      }
      const updatedViews = [...savedViews, newView]

      setViews(updatedViews)
      setActiveViewId(id)
      setOrder(srcOrder)
      setHidden(srcHidden)
      setGlobalFiltersState(srcFilters)
      setFilterLabelsState(srcLabels)
      setIsEditing(true)
      snapshot.current = {
        order: srcOrder,
        hidden: srcHidden,
        layout: live.current.layout
      }

      await commitConfig(
        updatedViews,
        id,
        did,
        srcOrder,
        srcHidden,
        srcFilters,
        srcLabels,
        versionRef.current,
        instancesRef.current,
        fixedRef.current,
        preferenceKeyRef.current,
        homeDefaultVersionRef.current
      )
    },
    []
  )

  const renameView = useCallback(async (id: string, name: string) => {
    const {
      views: v,
      activeViewId: aid,
      defaultViewId: did,
      order: o,
      hidden: h,
      globalFilters: g,
      filterLabels: fl
    } = live.current
    const updatedViews = v.map((x) => ({
      ...x,
      ...(x.id === id ? { name } : {}),
      ...(x.id === aid
        ? { order: o, hidden: h, globalFilters: g, filterLabels: fl }
        : {})
    }))
    setViews(updatedViews)
    await commitConfig(
      updatedViews,
      aid,
      did,
      o,
      h,
      g,
      fl,
      versionRef.current,
      instancesRef.current,
      fixedRef.current,
      preferenceKeyRef.current,
      homeDefaultVersionRef.current
    )
  }, [])

  const deleteView = useCallback(async (id: string) => {
    // The shipped default is not the user's to delete; it is how they get
    // back to the standard composition after customising.
    if (id === GC_DEFAULT_VIEW_ID) return
    const {
      views: v,
      activeViewId: aid,
      defaultViewId: did,
      order: o,
      hidden: h,
      globalFilters: g,
      filterLabels: fl
    } = live.current
    if (v.length <= 1) return

    const savedViews = v.map((view) =>
      view.id === aid
        ? { ...view, order: o, hidden: h, globalFilters: g, filterLabels: fl }
        : view
    )
    const updatedViews = savedViews.filter((x) => x.id !== id)
    const nextDefaultId = did === id ? (updatedViews[0]?.id ?? null) : did

    let nextActiveId = aid
    let nextOrder = o
    let nextHidden = h

    if (aid === id) {
      const fallback = updatedViews[0]
      nextActiveId = fallback?.id ?? null
      nextOrder = fallback?.order ?? []
      nextHidden = fallback?.hidden ?? []
      const nextFilters = fallback
        ? parseGlobalFilters(fallback)
        : createEmptyFilters()
      const nextLabels = fallback
        ? parseFilterLabels(fallback)
        : createEmptyFilterLabels()
      setActiveViewId(nextActiveId)
      setOrder(nextOrder)
      setHidden(nextHidden)
      setGlobalFiltersState(nextFilters)
      setFilterLabelsState(nextLabels)
      setIsEditing(false)
      snapshot.current = null
    }

    setViews(updatedViews)
    setDefaultViewId(nextDefaultId)
    const active = updatedViews.find((view) => view.id === nextActiveId)
    const nextFilters = active
      ? parseGlobalFilters(active)
      : createEmptyFilters()
    const nextLabels = active
      ? parseFilterLabels(active)
      : createEmptyFilterLabels()
    await commitConfig(
      updatedViews,
      nextActiveId,
      nextDefaultId,
      nextOrder,
      nextHidden,
      nextFilters,
      nextLabels,
      versionRef.current,
      instancesRef.current,
      fixedRef.current,
      preferenceKeyRef.current,
      homeDefaultVersionRef.current
    )
  }, [])

  const setDefault = useCallback(async (id: string) => {
    const {
      views: v,
      activeViewId: aid,
      order: o,
      hidden: h,
      globalFilters: g,
      filterLabels: fl
    } = live.current
    setDefaultViewId(id)
    await commitConfig(
      v,
      aid,
      id,
      o,
      h,
      g,
      fl,
      versionRef.current,
      instancesRef.current,
      fixedRef.current,
      preferenceKeyRef.current,
      homeDefaultVersionRef.current
    )
  }, [])

  const switchView = useCallback(async (id: string) => {
    const {
      views: v,
      activeViewId: aid,
      defaultViewId: did,
      order: o,
      hidden: h,
      globalFilters: g,
      filterLabels: fl
    } = live.current
    if (id === aid) return
    const nextView = v.find((x) => x.id === id)
    if (!nextView) return

    const savedViews = v.map((x) =>
      x.id === aid
        ? { ...x, order: o, hidden: h, globalFilters: g, filterLabels: fl }
        : x
    )
    const resolvedNext = savedViews.find((x) => x.id === id)!
    const nextFilters = parseGlobalFilters(resolvedNext)
    const nextLabels = parseFilterLabels(resolvedNext)

    setViews(savedViews)
    setActiveViewId(id)
    setOrder(resolvedNext.order)
    setHidden(resolvedNext.hidden)
    setLayoutState(resolvedNext.layout ?? [])
    setGlobalFiltersState(nextFilters)
    setFilterLabelsState(nextLabels)

    try {
      await commitConfig(
        savedViews,
        id,
        did,
        resolvedNext.order,
        resolvedNext.hidden,
        nextFilters,
        nextLabels,
        versionRef.current,
        instancesRef.current,
        fixedRef.current,
        preferenceKeyRef.current,
        homeDefaultVersionRef.current
      )
    } catch (err) {
      setViews(v)
      setActiveViewId(aid)
      setOrder(o)
      setHidden(h)
      setGlobalFiltersState(g)
      setFilterLabelsState(fl)
      throw err
    }
  }, [])

  const startEdit = useCallback((effectiveOrder?: string[]) => {
    const { order: o, hidden: h } = live.current
    const seedOrder = o.length ? o : (effectiveOrder ?? o)
    if (!o.length && effectiveOrder?.length) {
      setOrder(seedOrder)
    }
    snapshot.current = {
      order: seedOrder,
      hidden: h,
      layout: live.current.layout
    }
    setIsEditing(true)
  }, [])

  const cancel = useCallback(() => {
    if (snapshot.current) {
      setOrder(snapshot.current.order)
      setHidden(snapshot.current.hidden)
      setLayoutState(snapshot.current.layout)
      snapshot.current = null
    }
    setIsEditing(false)
  }, [])

  const reorder = useCallback((activeId: string, overId: string) => {
    setOrder((prev) => {
      const oldIdx = prev.indexOf(activeId)
      const newIdx = prev.indexOf(overId)
      if (oldIdx === -1 || newIdx === -1) return prev
      return arrayMove(prev, oldIdx, newIdx)
    })
    // The layout now HAS an order of its own. Without this the metric grid's
    // default arrangement, which applies only while no order was ever chosen,
    // re-sorts every drag away the moment it lands — the drop succeeds and
    // nothing moves.
    setHasStoredOrder(true)
  }, [])

  const toggleHidden = useCallback((id: string) => {
    setHidden((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }, [])

  const addTile = useCallback((id: string) => {
    setOrder((prev) => (prev.includes(id) ? prev : [...prev, id]))
    setHidden((prev) => prev.filter((x) => x !== id))
  }, [])

  const addWidget = useCallback((instance: WidgetInstanceV4) => {
    instancesRef.current[instance.slug] = instance
    setOrder((prev) =>
      prev.includes(instance.slug) ? prev : [...prev, instance.slug]
    )
    setHidden((prev) => prev.filter((x) => x !== instance.slug))
  }, [])

  const registerInstances = useCallback((instances: WidgetInstanceV4[]) => {
    for (const inst of instances) {
      if (!instancesRef.current[inst.slug])
        instancesRef.current[inst.slug] = inst
    }
  }, [])

  const getInstance = useCallback(
    (slug: string) => instancesRef.current[slug],
    []
  )

  /**
   * Completes the one-time migration: puts the catalog-resolved default
   * instances on GC Default, marks the config with `HOME_DEFAULT_VERSION` and
   * persists in one write, so the next load (and the widgets request, which
   * reads the SAVED config) sees a populated view. Nothing of the user's
   * other views is touched. A refusal leaves the draft on screen and the
   * marker unset, so the next load retries rather than silently losing it.
   */
  const seedDefaultWidgets = useCallback(
    async (instances: WidgetInstanceV4[], options?: { append?: boolean }) => {
      const {
        views: v,
        activeViewId: aid,
        defaultViewId: did,
        globalFilters: g,
        filterLabels: fl
      } = live.current
      for (const inst of instances) instancesRef.current[inst.slug] = inst
      const seeded = instances.map((inst) => inst.slug)
      const gcView = v.find((view) => view.id === GC_DEFAULT_VIEW_ID)
      // Appending keeps the view's own order and removals: only widgets it
      // does not hold yet are added, at the end.
      const order = options?.append
        ? [
            ...(gcView?.order ?? []),
            ...seeded.filter((slug) => !gcView?.order.includes(slug))
          ]
        : seeded
      const gcHidden = options?.append ? (gcView?.hidden ?? []) : []
      const updatedViews = v.map((view) =>
        view.id === GC_DEFAULT_VIEW_ID
          ? { ...view, order, hidden: gcHidden }
          : view
      )
      setViews(updatedViews)
      if (aid === GC_DEFAULT_VIEW_ID) {
        setOrder(order)
        setHidden(gcHidden)
        setHasStoredOrder(true)
      }
      setPendingDefaultSeed(false)
      const activeOrder =
        aid === GC_DEFAULT_VIEW_ID
          ? order
          : (updatedViews.find((view) => view.id === aid)?.order ?? [])
      const activeHidden =
        aid === GC_DEFAULT_VIEW_ID
          ? gcHidden
          : (updatedViews.find((view) => view.id === aid)?.hidden ?? [])
      try {
        await commitConfig(
          updatedViews,
          aid,
          did,
          activeOrder,
          activeHidden,
          g,
          fl,
          versionRef.current,
          instancesRef.current,
          fixedRef.current,
          preferenceKeyRef.current,
          HOME_DEFAULT_VERSION
        )
        homeDefaultVersionRef.current = HOME_DEFAULT_VERSION
      } catch (err) {
        setPendingDefaultSeed(true)
        throw err
      }
    },
    []
  )

  // A second press while the first request is in flight would write the same
  // draft twice and, worse, could land the two responses out of order. The
  // ref is what actually guards it: `saving` state has not re-rendered yet
  // when a double click arrives inside one frame.
  const save = useCallback(async () => {
    if (savingRef.current) return
    savingRef.current = true
    setSaving(true)
    try {
      const {
        views: v,
        activeViewId: aid,
        defaultViewId: did,
        order: o,
        hidden: h,
        layout: lay,
        globalFilters: g,
        filterLabels: fl
      } = live.current
      // One write carries additions, removals, order AND geometry, so a save
      // can never persist half a draft.
      const updatedViews = v.map((x) =>
        x.id === aid
          ? {
              ...x,
              order: o,
              hidden: h,
              layout: lay,
              globalFilters: g,
              filterLabels: fl
            }
          : x
      )
      const config = buildConfig(
        versionRef.current,
        updatedViews,
        aid,
        did,
        instancesRef.current,
        fixedRef.current,
        homeDefaultVersionRef.current
      )
      const response = await patchUIPreferences(
        buildPreferencesPatch(preferenceKeyRef.current, config)
      )
      // Editing mode and the snapshot both survive a refusal, so the draft is
      // still on screen and still savable when the user retries.
      if (!response.ok) throw new Error("save failed")
      setViews(updatedViews)
      setIsEditing(false)
      snapshot.current = null
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }, [])

  // The draft alone. `restoreDefault` below writes the same empty view through
  // to the server; this one stops at the draft so Save stays the only thing
  // that changes what is stored.
  const resetDraftToDefault = useCallback(() => {
    setOrder([])
    setHidden([])
    setLayoutState([])
  }, [])

  const resetDraftTo = useCallback(
    (draft: { order: string[]; hidden?: string[] }) => {
      setOrder(draft.order)
      setHidden(draft.hidden ?? [])
      setLayoutState([])
    },
    []
  )

  const setLayout = useCallback((next: DashboardLayoutItemV4[]) => {
    setLayoutState(next)
  }, [])

  // Restores the out-of-the-box layout, not another saved view: an empty view is
  // what makes the backend re-resolve role → company → system defaults.
  const restoreDefault = useCallback(async () => {
    const {
      views: v,
      activeViewId: aid,
      defaultViewId: did,
      order: o,
      hidden: h,
      globalFilters: g,
      filterLabels: fl
    } = live.current
    if (aid == null) return
    const emptyFilters = createEmptyFilters()
    const emptyLabels = createEmptyFilterLabels()
    setOrder([])
    setHidden([])
    setGlobalFiltersState(emptyFilters)
    setFilterLabelsState(emptyLabels)
    const updatedViews = v.map((view) =>
      view.id === aid
        ? {
            ...view,
            order: [],
            hidden: [],
            globalFilters: emptyFilters,
            filterLabels: emptyLabels
          }
        : view
    )
    setViews(updatedViews)
    setIsEditing(false)
    snapshot.current = null
    try {
      await commitConfig(
        updatedViews,
        aid,
        did,
        [],
        [],
        emptyFilters,
        emptyLabels,
        versionRef.current,
        instancesRef.current,
        fixedRef.current,
        preferenceKeyRef.current,
        homeDefaultVersionRef.current
      )
    } catch (err) {
      setViews(v)
      setOrder(o)
      setHidden(h)
      setGlobalFiltersState(g)
      setFilterLabelsState(fl)
      throw err
    }
  }, [])

  // Editing began from `snapshot`; anything else on the draft is unsaved work.
  const isDirty = Boolean(
    snapshot.current &&
    (snapshot.current.order.join("\u0000") !== order.join("\u0000") ||
      [...snapshot.current.hidden].sort().join("\u0000") !==
        [...hidden].sort().join("\u0000") ||
      serializeLayout(snapshot.current.layout) !== serializeLayout(layout))
  )

  return {
    views,
    activeView,
    activeViewId,
    defaultViewId,
    isEditing,
    hasStoredOrder,
    order,
    hidden,
    globalFilters,
    filterLabels,
    initFromConfig,
    pendingDefaultSeed,
    pendingSeedFromVersion,
    seedDefaultWidgets,
    persistGlobalFilters,
    createView,
    renameView,
    deleteView,
    setDefault,
    switchView,
    reorder,
    layout,
    setLayout,
    toggleHidden,
    addTile,
    addWidget,
    registerInstances,
    getInstance,
    startEdit,
    cancel,
    save,
    saving,
    isDirty,
    resetDraftToDefault,
    resetDraftTo,
    restoreDefault
  }
}
